/**
 * Agent 管理 API 响应的 Zod 运行时校验 Schema
 *
 * 校验范围：Agent CRUD 响应
 * 用途：在 API 边界捕获后端返回结构变化
 */
import { z } from 'zod'

/**
 * Agent 基础信息 Schema
 * 对应 src/api/agents.ts 的 Agent 接口
 */
export const AgentSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.string().nullable(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  avatar_url: z.string().nullable(),
  agent_type: z.string(),
  tags: z.array(z.string()).nullable(),
  endpoint: z.string().nullable(),
  invoke_method: z.string(),
  input_schema: z.record(z.string(), z.unknown()).nullable(),
  output_schema: z.record(z.string(), z.unknown()).nullable(),
  capabilities: z.array(z.string()).nullable(),
  status: z.enum(['active', 'inactive', 'deprecated']),
  is_system: z.boolean(),
  is_callable: z.boolean(),
  timeout_seconds: z.number(),
  max_retries: z.number(),
  call_count: z.number(),
  success_count: z.number(),
  success_rate: z.number(),
  avg_latency_ms: z.number().nullable(),
  version: z.string(),
  config: z.record(z.string(), z.unknown()).nullable(),
  // 治理口径显式契约（PR5 / Phase 3）。.default() 容忍灰度期未部署后端的旧响应。
  business: z.boolean().default(false),
  surface_type: z
    .enum(['business', 'skill', 'internal', 'fallback', 'deprecated'])
    .nullable()
    .default(null),
  parent_agent_id: z.string().nullable().default(null),
  list_visible: z.boolean().nullable().default(null),
  workspace_visible: z.boolean().nullable().default(null),
  canonical_id: z.string().nullable().default(null),
  replaced_by: z.string().nullable().default(null),
  created_at: z.string(),
  updated_at: z.string(),
})

export type AgentFromSchema = z.infer<typeof AgentSchema>

/**
 * Agent 列表响应 Schema
 * 对应 src/api/agents.ts 的 AgentListResponse 接口
 */
export const AgentListResponseSchema = z.object({
  agents: z.array(AgentSchema),
  total: z.number(),
})

export type AgentListResponseFromSchema = z.infer<typeof AgentListResponseSchema>
